import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  TextField,
  CircularProgress,
  IconButton
} from '@mui/material';
import { toast } from 'react-toastify';
import CloseIcon from '@mui/icons-material/Close';
import 'react-toastify/dist/ReactToastify.css';
import { ApiService } from './ApiService';

export const ModalNewPassword = ({ open, onClose }) => {
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);

  // --- Campos de Paso 0 (correo)
  const [email, setEmail] = useState('');

  // --- Campos de Paso 1 (código con OTP style)
  const [otp, setOtp] = useState(['', '', '', '', '', '']);

  // --- Campos de Paso 2 (nueva contraseña)
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Resetear estados al abrir el modal
  useEffect(() => {
    if (open) {
      setStep(0);
      setEmail('');
      setOtp(['', '', '', '', '', '']);
      setNewPassword('');
      setConfirmPassword('');
      setLoading(false);
    }
  }, [open]);

  /* -------------------------------------------
   *  PASO 0: Enviar correo a API
   * -----------------------------------------*/
  const handleCheckEmail = async () => {
    if (!email.trim()) {
      toast.error('El campo correo es obligatorio');
      return;
    }

    // Llamar a la API real:
    try {
      setLoading(true);
      const body = { email_User: email.trim() };
      const response = await ApiService.fetchData('api/auth/password-reset/code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      setLoading(false);
      if (!response) {
        toast.error('No hay respuesta del servidor');
        return;
      }
      if (response.code === 200) {
        toast.success(response.message || 'Código enviado al correo');
        setStep(1);
      } else {
        toast.error(response.message || 'Error al enviar código');
      }
    } catch (error) {
      console.error(error);
      toast.error('Error al procesar la solicitud');
      setLoading(false);
    }
  };

  /* -------------------------------------------
   *  PASO 1: Validar campos OTP (no más local-check)
   * -----------------------------------------*/
  const handleChangeOtp = (index, value) => {
    // Solo números
    if (!/^\d*$/.test(value)) return;
    const newArr = [...otp];
    // Solo último dígito
    newArr[index] = value ? value.slice(-1) : '';
    setOtp(newArr);

    // Mover el focus si escribe un dígito
    if (value && index < 5) {
      const next = document.getElementById(`otp-${index + 1}`);
      if (next) next.focus();
    }
  };

  const handleGoNextFromCode = () => {
    const combinedOtp = otp.join('');
    if (combinedOtp.length < 6) {
      toast.error('Debes ingresar los 6 dígitos');
      return;
    }
    // NO llamamos API aquí, se validará al final
    setStep(2);
  };

  /* -------------------------------------------
   *  PASO 2: Cambiar contraseña
   * -----------------------------------------*/
  const handleChangePassword = async () => {
    if (!newPassword.trim() || !confirmPassword.trim()) {
      toast.error('Todos los campos son obligatorios');
      return;
    }

    // Validar requisitos: mínimo 8 caracteres y al menos 1 dígito
    const regex = /^(?=.*\d).{8,}$/;
    if (!regex.test(newPassword)) {
      toast.error('La contraseña debe tener al menos 8 caracteres y un número');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Las contraseñas no coinciden');
      return;
    }

    const combinedOtp = otp.join('');
    if (combinedOtp.length < 6) {
      toast.error('Código de verificación inválido');
      return;
    }

    // Llamada final: PUT /api/user/password-reset
    try {
      setLoading(true);
      const body = {
        email_User: email.trim(),
        password: newPassword,
        password_confirmation: confirmPassword,
        recover_code: combinedOtp
      };
      const response = await ApiService.fetchData('api/user/password-reset', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      setLoading(false);
      if (!response) {
        toast.error('Sin respuesta del servidor');
        return;
      }
      if (response.code === 200) {
        toast.success(response.message || 'Contraseña actualizada exitosamente');
        onClose();
      } else {
        toast.error(response.message || 'Error al actualizar contraseña');
      }
    } catch (error) {
      console.error(error);
      toast.error('Error al cambiar contraseña');
      setLoading(false);
    }
  };

  /* -------------------------------------------
   *  Render de cada paso
   * -----------------------------------------*/
  const renderStepContent = () => {
    switch (step) {
      // -----------------------------------
      // Paso 0: Solicitar correo
      case 0:
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <DialogTitle sx={{ color: '#000', fontSize: '16px', padding: 0, position: 'relative', top: '15px' }}>
              Correo electrónico
              <span style={{ color: 'gray', fontSize: '14px' }}> (Te enviaremos un código)</span>
            </DialogTitle>
            <TextField
              type="email"
              size="small"
              variant="outlined"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              fullWidth
              sx={{ '& input': { fontSize: '16px' } }}
            />

            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8 }}>
              <Button variant="outlined" onClick={onClose}>
                Cancelar
              </Button>
              <Button variant="contained" color="error" onClick={handleCheckEmail} disabled={loading}>
                {loading ? (
                  <>
                    <CircularProgress size={20} sx={{ mr: 1, color: '#fff' }} />
                    Cargando...
                  </>
                ) : (
                  'Siguiente'
                )}
              </Button>
            </div>
          </div>
        );

      // -----------------------------------
      // Paso 1: Capturar código (6 dígitos)
      case 1:
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <DialogTitle sx={{ color: '#000', fontSize: '16px', padding: 0, position: 'relative', top: '15px' }}>
              Código
              <span style={{ color: 'gray', fontSize: '14px' }}> (Revisa tu correo)</span>
            </DialogTitle>
            <div style={{ display: 'flex', justifyContent: 'center', gap: 8 }}>
              {otp.map((digit, i) => (
                <TextField
                  key={i}
                  id={`otp-${i}`}
                  variant="outlined"
                  size="small"
                  value={digit}
                  onChange={(e) => handleChangeOtp(i, e.target.value)}
                  onKeyDown={(e) => {
                    // Mover focus con backspace/flechas
                    if (e.key === 'Backspace' && !digit && i > 0) {
                      const prev = document.getElementById(`otp-${i - 1}`);
                      if (prev) prev.focus();
                    }
                    if (e.key === 'ArrowLeft' && i > 0) {
                      const prev = document.getElementById(`otp-${i - 1}`);
                      if (prev) prev.focus();
                    }
                    if (e.key === 'ArrowRight' && i < 5) {
                      const next = document.getElementById(`otp-${i + 1}`);
                      if (next) next.focus();
                    }
                  }}
                  inputProps={{
                    maxLength: 1,
                    style: { textAlign: 'center', width: '40px', fontSize: '18px' }
                  }}
                />
              ))}
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8 }}>
              <Button
                variant="outlined"
                onClick={() => {
                  setStep(0);
                  setOtp(['', '', '', '', '', '']); // Reiniciar
                }}
              >
                Atrás
              </Button>
              <Button variant="contained" color="error" onClick={handleGoNextFromCode} disabled={loading}>
                {loading ? (
                  <>
                    <CircularProgress size={20} sx={{ mr: 1, color: '#fff' }} />
                    Cargando...
                  </>
                ) : (
                  'Siguiente'
                )}
              </Button>
            </div>
          </div>
        );

      // -----------------------------------
      // Paso 2: Nueva contraseña
      case 2:
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <DialogTitle sx={{ color: '#000', fontSize: '16px', padding: 0, position: 'relative', top: '15px' }}>
              Nueva Contraseña
            </DialogTitle>
            <TextField
              type="password"
              size="small"
              variant="outlined"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              fullWidth
            />

            <DialogTitle sx={{ color: '#000', fontSize: '16px', padding: 0, position: 'relative', top: '15px' }}>
              Confirmar Contraseña
              <span style={{ color: 'gray', fontSize: '14px' }}> (Vuelve a escribirla)</span>
            </DialogTitle>
            <TextField
              type="password"
              size="small"
              variant="outlined"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              fullWidth
            />

            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8 }}>
              <Button
                variant="outlined"
                onClick={() => {
                  setStep(0);
                  setOtp(['', '', '', '', '', '']); // Reiniciar
                  setNewPassword('');
                  setConfirmPassword('');
                }}
              >
                Cancelar
              </Button>
              <Button variant="contained" color="error" onClick={handleChangePassword} disabled={loading}>
                {loading ? (
                  <>
                    <CircularProgress size={20} sx={{ mr: 1, color: '#fff' }} />
                    Cargando...
                  </>
                ) : (
                  'Guardar'
                )}
              </Button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog open={open} maxWidth="xs" fullWidth disableEscapeKeyDown  
    onClose={(event, reason) => {
      if (reason && reason === 'backdropClick') {
        return; // Evita cerrar modal al hacer clic afuera
      }
      onClose();
    }}>
      {/* Encabezado rojo con botón X */}
      <DialogTitle
        sx={{
          backgroundColor: '#d32f2f',
          color: 'white',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        Recuperar Contraseña
        <IconButton
          onClick={onClose}
          sx={{ color: 'white', position: 'relative', left: '15px', bottom: '5px' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ mt: 2 }}>
        {renderStepContent()}
      </DialogContent>
    </Dialog>
  );
};
